import React, { useEffect } from 'react';
import { BrowserRouter as Router, Navigate, Routes, Route, useLocation } from 'react-router-dom';
import { ThemeProvider } from './context/ThemeContext';
import { AuthProvider, useAuth } from './context/AuthContext';
import { GameProvider } from './context/GameContext';
import { ToastProvider } from './components/ui/Toast';
import Layout from './components/layout/Layout';
import OnboardingFlow from './components/modals/OnboardingFlow';
import LoadingScreen from './components/ui/LoadingScreen';
import GameEngine from './components/GameEngine';
import { StillroomPlaces } from './MindGardenReference';

import Overview from './pages/Overview';
import Garden from './pages/Garden';
import Production from './pages/Production';
import Upgrades from './pages/Upgrades';
import FocusSession from './pages/FocusSession';
import StreakDaily from './pages/StreakDaily';
import DailyWatering from './pages/DailyWatering';
import GardenPlot from './pages/GardenPlot';
import ThoughtGarden from './pages/ThoughtGarden';
import HabitGarden from './pages/HabitGarden';
import TheGreenhouse from './pages/TheGreenhouse';
import WeedList from './pages/WeedList';
import Analytics from './pages/Analytics';
import Settings from './pages/Settings';
import CalendarPage from './pages/CalendarPage';
import TheGrove from './pages/TheGrove';
import DailyPlanner from './pages/DailyPlanner';
import TrainingCards from './pages/TrainingCards';
import PrestigePage from './pages/PrestigePage';
import Marketplace from './pages/Marketplace';
import MarketplaceSuccess from './pages/MarketplaceSuccess';
import DividendGarden from './pages/DividendGarden';
import GatekeeperAI from './pages/GatekeeperAI';
import Welcome from './pages/Welcome';

function AppRoutes({ showOnboarding, completeOnboarding }) {
  const location = useLocation();
  const isWelcome = location.pathname === '/welcome';

  const routes = (
    <Routes>
      <Route path="/" element={<Overview />} />
      <Route path="/plan" element={<DailyPlanner />} />
      <Route path="/studio" element={<FocusSession />} />
      <Route path="/garden" element={<HabitGarden />} />
      <Route path="/journal" element={<ThoughtGarden />} />
      <Route path="/membership" element={<Marketplace />} />
      <Route path="/settings" element={<Settings />} />

      <Route path="/focus" element={<FocusSession />} />
      <Route path="/classic/garden" element={<Garden />} />
      <Route path="/production" element={<Production />} />
      <Route path="/upgrades" element={<Upgrades />} />
      <Route path="/prestige" element={<PrestigePage />} />
      <Route path="/marketplace" element={<Marketplace />} />
      <Route path="/marketplace/success" element={<MarketplaceSuccess />} />
      <Route path="/gatekeeper" element={<GatekeeperAI />} />
      <Route path="/streak" element={<StreakDaily />} />
      <Route path="/watering" element={<DailyWatering />} />
      <Route path="/plots" element={<GardenPlot />} />
      <Route path="/dividend-garden" element={<DividendGarden />} />
      <Route path="/thought-garden" element={<ThoughtGarden />} />
      <Route path="/habit-garden" element={<HabitGarden />} />
      <Route path="/the-greenhouse" element={<TheGreenhouse />} />
      <Route path="/weed-list" element={<WeedList />} />
      <Route path="/analytics" element={<Analytics />} />
      <Route path="/calendar" element={<CalendarPage />} />
      <Route path="/planner" element={<DailyPlanner />} />
      <Route path="/the-grove" element={<TheGrove />} />
      <Route path="/training-cards" element={<TrainingCards />} />
      <Route path="/legacy-leo" element={<Navigate to="/" replace />} />
      <Route path="*" element={<Overview />} />
    </Routes>
  );

  if (isWelcome) {
    return (
      <Routes>
        <Route path="/welcome" element={<Welcome />} />
      </Routes>
    );
  }

  return (
    <>
      <Layout>{routes}</Layout>
      <OnboardingFlow isOpen={showOnboarding} onComplete={completeOnboarding} />
    </>
  );
}

function AppContent() {
  const { isLoading, showOnboarding, completeOnboarding } = useAuth();

  useEffect(() => {
    const handleFocusIn = (e) => {
      if (e.target.closest('#aminos-chat-widget')) {
        document.body.classList.add('chat-widget-active');
      } else {
        document.body.classList.remove('chat-widget-active');
        if (e.target.matches('input, textarea, [contenteditable="true"]')) {
          e.target.classList.add('keyboard-event-protection');
          document.body.classList.add('app-input-active');
        }
      }
    };
    const handleFocusOut = (e) => {
      if (!e.target.closest('#aminos-chat-widget')) {
        e.target.classList.remove('keyboard-event-protection');
        document.body.classList.remove('app-input-active');
      }
    };
    const handleKeyDown = (e) => {
      if (
        e.code === 'Space' &&
        e.target.matches('input, textarea, [contenteditable="true"]') &&
        !e.target.closest('#aminos-chat-widget')
      ) {
        e.stopPropagation();
      }
    };
    document.addEventListener('focusin', handleFocusIn);
    document.addEventListener('focusout', handleFocusOut);
    document.addEventListener('keydown', handleKeyDown, true);
    return () => {
      document.removeEventListener('focusin', handleFocusIn);
      document.removeEventListener('focusout', handleFocusOut);
      document.removeEventListener('keydown', handleKeyDown, true);
    };
  }, []);

  if (isLoading) return <LoadingScreen />;

  return (
    <GameProvider>
      <GameEngine />
      <Router>
        <AppRoutes
          showOnboarding={showOnboarding}
          completeOnboarding={completeOnboarding}
        />
      </Router>
    </GameProvider>
  );
}

function App() {
  if (['/', '/cafe', '/cafe/'].includes(window.location.pathname)) return <StillroomPlaces />;
  return (
    <ThemeProvider>
      <ToastProvider>
        <AuthProvider>
          <AppContent />
        </AuthProvider>
      </ToastProvider>
    </ThemeProvider>
  );
}

export default App;